import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import Image from '../AppImage';

const InteractiveImageHotspot = ({ 
  imageSrc, 
  imageAlt = "Bike analysis", 
  hotspots = [],
  onHotspotClick = () => {},
  className = ""
}) => {
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateImageDimensions = () => {
      if (imageRef?.current && containerRef?.current) {
        const rect = imageRef?.current?.getBoundingClientRect();
        setImageDimensions({
          width: rect?.width,
          height: rect?.height
        });
      }
    };

    if (imageLoaded) {
      updateImageDimensions();
      window.addEventListener('resize', updateImageDimensions);
      return () => window.removeEventListener('resize', updateImageDimensions);
    }
  }, [imageLoaded]);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleHotspotClick = (hotspot, event) => {
    event?.stopPropagation();
    setActiveHotspot(activeHotspot?.id === hotspot?.id ? null : hotspot);
    onHotspotClick(hotspot);
  };

  const handleContainerClick = () => {
    setActiveHotspot(null);
  };

  const getHotspotPosition = (hotspot) => {
    if (!imageDimensions?.width || !imageDimensions?.height) return {};
    
    return {
      left: `${(hotspot?.x / 100) * imageDimensions?.width}px`,
      top: `${(hotspot?.y / 100) * imageDimensions?.height}px`,
    };
  };

  const getTooltipPosition = (hotspot) => {
    if (!imageDimensions?.width || !imageDimensions?.height) return {};
    
    const hotspotX = (hotspot?.x / 100) * imageDimensions?.width;
    const hotspotY = (hotspot?.y / 100) * imageDimensions?.height;
    
    // Position tooltip to avoid edges
    const tooltipWidth = 280;
    const tooltipHeight = 200;
    
    let left = hotspotX + 20;
    let top = hotspotY - tooltipHeight / 2;
    
    // Adjust if tooltip would go off right edge
    if (left + tooltipWidth > imageDimensions?.width) {
      left = hotspotX - tooltipWidth - 20;
    }
    
    // Adjust if tooltip would go off top/bottom edges
    if (top < 0) {
      top = 10;
    } else if (top + tooltipHeight > imageDimensions?.height) {
      top = imageDimensions?.height - tooltipHeight - 10;
    }
    
    return {
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  const HotspotMarker = ({ hotspot, isActive }) => (
    <button
      className={`absolute w-6 h-6 -ml-3 -mt-3 rounded-full border-2 transition-all duration-200 interactive-scale z-10 ${
        isActive 
          ? 'bg-accent border-accent-foreground shadow-lg scale-125' 
          : 'bg-accent/80 border-white shadow-md hover:scale-110'
      }`}
      style={getHotspotPosition(hotspot)}
      onClick={(e) => handleHotspotClick(hotspot, e)}
      aria-label={`View ${hotspot?.component} details`}
    >
      <div className="w-full h-full flex items-center justify-center">
        <div className={`w-2 h-2 rounded-full ${
          isActive ? 'bg-accent-foreground' : 'bg-white'
        }`} />
      </div>
      
      {/* Pulse animation for inactive hotspots */}
      {!isActive && (
        <div className="absolute inset-0 rounded-full bg-accent animate-ping opacity-30" />
      )}
    </button>
  );

  const HotspotTooltip = ({ hotspot }) => (
    <div
      className="absolute bg-card border border-border rounded-lg shadow-lg p-4 w-70 z-20 animate-fade-in"
      style={getTooltipPosition(hotspot)}
    >
      {/* Arrow pointing to hotspot */}
      <div className="absolute w-3 h-3 bg-card border-l border-t border-border transform rotate-45 -translate-x-1/2"
           style={{
             left: '20px',
             top: '50%',
             marginTop: '-6px'
           }} />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold text-foreground text-sm">
              {hotspot?.component}
            </h4>
            <p className="text-xs text-muted-foreground">
              {hotspot?.category}
            </p>
          </div>
          <button
            onClick={() => setActiveHotspot(null)}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <Icon name="X" size={14} />
          </button>
        </div>

        {/* Current Status */}
        <div className="mb-3">
          <div className="flex items-center space-x-2 mb-1">
            <Icon 
              name={hotspot?.status === 'good' ? 'CheckCircle' : 
                    hotspot?.status === 'upgrade' ? 'AlertTriangle' : 'XCircle'} 
              size={14} 
              color={hotspot?.status === 'good' ? 'var(--color-success)' : 
                     hotspot?.status === 'upgrade' ? 'var(--color-warning)' : 'var(--color-error)'}
            />
            <span className="text-xs font-medium">
              {hotspot?.status === 'good' ? 'Good Condition' : 
               hotspot?.status === 'upgrade' ? 'Upgrade Recommended' : 'Needs Attention'}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            {hotspot?.description}
          </p>
        </div>

        {/* Upgrade Info */}
        {hotspot?.upgrade && (
          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-foreground">
                Recommended Upgrade
              </span>
              <span className="text-xs font-mono text-accent">
                ${hotspot?.upgrade?.price}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">
              {hotspot?.upgrade?.name}
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => onHotspotClick(hotspot, 'view-details')}
                className="flex-1 px-2 py-1 text-xs bg-accent text-accent-foreground rounded hover:bg-accent/90 transition-colors interactive-scale"
              >
                View Details
              </button>
              <button
                onClick={() => onHotspotClick(hotspot, 'add-to-wishlist')}
                className="px-2 py-1 text-xs border border-border rounded hover:bg-muted transition-colors interactive-scale"
              >
                <Icon name="Heart" size={12} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div 
      ref={containerRef}
      className={`relative inline-block ${className}`}
      onClick={handleContainerClick}
    >
      {/* Main Image */}
      <Image
        ref={imageRef}
        src={imageSrc}
        alt={imageAlt}
        className="w-full h-auto rounded-lg"
        onLoad={handleImageLoad}
      />
      {/* Loading State */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-muted rounded-lg flex items-center justify-center">
          <div className="flex items-center space-x-2 text-muted-foreground">
            <Icon name="Loader2" size={20} className="animate-spin" />
            <span className="text-sm">Analyzing image...</span>
          </div>
        </div>
      )}
      {/* Hotspot Markers */}
      {imageLoaded && hotspots?.map((hotspot) => (
        <HotspotMarker
          key={hotspot?.id}
          hotspot={hotspot}
          isActive={activeHotspot?.id === hotspot?.id}
        />
      ))}
      {/* Active Tooltip */}
      {imageLoaded && activeHotspot && (
        <HotspotTooltip hotspot={activeHotspot} />
      )}
      {/* Hotspot Legend (Mobile) */}
      {imageLoaded && hotspots?.length > 0 && (
        <div className="lg:hidden mt-4 p-4 bg-card border border-border rounded-lg">
          <h4 className="text-sm font-semibold text-foreground mb-3">
            Detected Components ({hotspots?.length})
          </h4>
          <div className="space-y-2">
            {hotspots?.map((hotspot) => (
              <button
                key={hotspot?.id}
                onClick={(e) => handleHotspotClick(hotspot, e)}
                className={`w-full flex items-center justify-between p-2 rounded-md transition-colors interactive-scale ${
                  activeHotspot?.id === hotspot?.id 
                    ? 'bg-accent text-accent-foreground' 
                    : 'hover:bg-muted'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-accent rounded-full" />
                  <div className="text-left">
                    <div className="text-sm font-medium">{hotspot?.component}</div>
                    <div className="text-xs text-muted-foreground">{hotspot?.category}</div>
                  </div>
                </div>
                <Icon 
                  name={hotspot?.status === 'good' ? 'CheckCircle' : 
                        hotspot?.status === 'upgrade' ? 'AlertTriangle' : 'XCircle'} 
                  size={16} 
                  color={hotspot?.status === 'good' ? 'var(--color-success)' : 
                         hotspot?.status === 'upgrade' ? 'var(--color-warning)' : 'var(--color-error)'}
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveImageHotspot;